import React, { useMemo } from 'react';
import { useTokens } from '@barracuda-internal/bds-core';

/**
 * Minimal inline-SVG sparkline. No deps.
 *
 * Props:
 *   data:     array of numbers, oldest -> newest
 *   width:    px (default 160)
 *   height:   px (default 40)
 *   strokeColor, fillColor: optional overrides; default = BDS chart series 2
 *
 * Renders an area path under a line. Y-axis auto-scales to the series max
 * (with a floor of 1 so a flat-zero series renders along the baseline
 * rather than NaN). When all values are 0 we render a faint baseline.
 */
const Sparkline = ({
  data,
  width = 160,
  height = 40,
  strokeColor,
  fillColor,
}) => {
  const tokens = useTokens();
  // Default to the BDS chart series 2 palette (purple) — matches the
  // platform/admin dashboards' "Scans" line so the visual language is
  // consistent. Range100 = solid line; Range20 = pre-alpha'd fill.
  const stroke =
    strokeColor || tokens.chartsColorSeries2Range100 || '#7E3FBF';
  const fill = fillColor || tokens.chartsColorSeries2Range20 || 'rgba(126,63,191,0.2)';

  const { linePath, areaPath, allZero } = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) {
      return { linePath: '', areaPath: '', allZero: true };
    }
    const max = Math.max(...data, 1);
    const minBound = 0;
    const xStep = data.length > 1 ? width / (data.length - 1) : 0;
    const yFor = (v) => {
      const ratio = (v - minBound) / (max - minBound);
      // small top padding so peaks don't touch the edge
      const padded = 0.1 + ratio * 0.85;
      return height - padded * height;
    };

    const points = data.map((v, i) => [i * xStep, yFor(v)]);
    const line = points
      .map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`)
      .join(' ');
    const area = `${line} L ${(points.at(-1)[0]).toFixed(2)} ${height} L 0 ${height} Z`;

    return {
      linePath: line,
      areaPath: area,
      allZero: data.every((v) => v === 0),
    };
  }, [data, width, height]);

  if (allZero) {
    return (
      <svg width={width} height={height} aria-label="No activity">
        <line
          x1="0"
          y1={height - 1}
          x2={width}
          y2={height - 1}
          stroke={tokens.aliasesColorBorderStaticContainerSubtle || '#ddd'}
          strokeWidth="1"
        />
      </svg>
    );
  }

  return (
    <svg width={width} height={height} aria-label="Scan activity, last 24 hours">
      <path d={areaPath} fill={fill} stroke="none" />
      <path d={linePath} fill="none" stroke={stroke} strokeWidth="1.5" />
    </svg>
  );
};

export default Sparkline;
